import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { CartLineModel, CartModel, ProductModel } from "@insite/client-framework/Types/ApiModels";

export interface GA4Item {
    item_id: string;
    item_name: string;
    item_brand?: string;
    item_category?: string;
    item_variant?: string;
    item_list_id?: string;
    item_list_name?: string;
    price?: number;
    quantity?: number;
    index?: number;
    discount?: number;
}

interface GA4ItemOptions {
    index?: number;
    listId?: string;
    listName?: string;
    quantity?: number;
}

function getDataLayer(): any[] | undefined {
    if (typeof window === "undefined") {
        return undefined;
    }
    return (window as any).dataLayer;
}

function clearAndPush(eventData: Record<string, any>): void {
    const dataLayer = getDataLayer();
    if (!dataLayer) {
        return;
    }
    dataLayer.push({ ecommerce: null });
    dataLayer.push(eventData);
}

export function getCurrencyCode(state: ApplicationState): string {
    return getSession(state)?.currency?.currencyCode || "USD";
}

export function buildGA4ItemFromProduct(product: ProductModel, opts?: GA4ItemOptions): GA4Item {
    const item: GA4Item = {
        item_id: product.productNumber,
        item_name: product.productTitle,
    };

    if (product.brand?.name) {
        item.item_brand = product.brand.name;
    }

    if (product.unitListPrice) {
        item.price = product.unitListPrice;
    }

    if (opts?.index !== undefined) {
        item.index = opts.index;
    }
    if (opts?.listId) {
        item.item_list_id = opts.listId;
    }
    if (opts?.listName) {
        item.item_list_name = opts.listName;
    }
    if (opts?.quantity !== undefined) {
        item.quantity = opts.quantity;
    }

    return item;
}

export function buildGA4ItemFromCartLine(cartLine: CartLineModel, opts?: GA4ItemOptions): GA4Item {
    const item: GA4Item = {
        item_id: cartLine.erpNumber,
        item_name: cartLine.shortDescription,
    };

    if (cartLine.brand?.name) {
        item.item_brand = cartLine.brand.name;
    }

    if (cartLine.pricing?.unitNetPrice) {
        item.price = cartLine.pricing.unitNetPrice;
    }

    if (cartLine.qtyOrdered) {
        item.quantity = cartLine.qtyOrdered;
    }

    if (opts?.index !== undefined) {
        item.index = opts.index;
    }
    if (opts?.listId) {
        item.item_list_id = opts.listId;
    }
    if (opts?.listName) {
        item.item_list_name = opts.listName;
    }

    return item;
}

export function buildGA4ItemsFromCartLines(cartLines: CartLineModel[] | null | undefined): GA4Item[] {
    if (!cartLines?.length) {
        return [];
    }
    return cartLines.map((line, index) => buildGA4ItemFromCartLine(line, { index }));
}

let lastTrackedViewItemListKey = "";

export function trackGA4ViewItemList(
    products: ProductModel[],
    listId: string,
    listName: string,
    currency: string,
) {
    const dedupKey = `${listId}_${products.map(p => p.id).join(",")}`;
    if (dedupKey === lastTrackedViewItemListKey) {
        return;
    }
    lastTrackedViewItemListKey = dedupKey;

    const items = products.map((product, index) =>
        buildGA4ItemFromProduct(product, { index, listId, listName }),
    );

    clearAndPush({
        event: "view_item_list",
        ecommerce: {
            currency,
            item_list_id: listId,
            item_list_name: listName,
            items,
        },
    });
}

export function trackGA4SelectItem(
    product: ProductModel,
    listId: string,
    listName: string,
    index: number,
    currency: string,
) {
    const item = buildGA4ItemFromProduct(product, { index, listId, listName });

    clearAndPush({
        event: "select_item",
        ecommerce: {
            currency,
            item_list_id: listId,
            item_list_name: listName,
            items: [item],
        },
    });
}

let lastTrackedViewItemId = "";

export function trackGA4ViewItem(product: ProductModel, currency: string) {
    if (product.id === lastTrackedViewItemId) {
        return;
    }
    lastTrackedViewItemId = product.id;

    const item = buildGA4ItemFromProduct(product);

    clearAndPush({
        event: "view_item",
        ecommerce: {
            currency,
            value: product.unitListPrice || 0,
            items: [item],
        },
    });
}

export function trackGA4AddToCart(items: GA4Item[], currency: string, value: number) {
    clearAndPush({
        event: "add_to_cart",
        ecommerce: {
            currency,
            value,
            items,
        },
    });
}

export function trackGA4RemoveFromCart(items: GA4Item[], currency: string, value: number) {
    clearAndPush({
        event: "remove_from_cart",
        ecommerce: {
            currency,
            value,
            items,
        },
    });
}

export function trackGA4BeginCheckout(
    items: GA4Item[],
    currency: string,
    value: number,
    coupon?: string,
) {
    clearAndPush({
        event: "begin_checkout",
        ecommerce: {
            currency,
            value,
            ...(coupon ? { coupon } : {}),
            items,
        },
    });
}

export function trackGA4AddShippingInfo(
    items: GA4Item[],
    currency: string,
    value: number,
    shippingTier: string,
    coupon?: string,
) {
    clearAndPush({
        event: "add_shipping_info",
        ecommerce: {
            currency,
            value,
            shipping_tier: shippingTier,
            ...(coupon ? { coupon } : {}),
            items,
        },
    });
}

export function trackGA4AddPaymentInfo(
    items: GA4Item[],
    currency: string,
    value: number,
    paymentType: string,
    coupon?: string,
) {
    clearAndPush({
        event: "add_payment_info",
        ecommerce: {
            currency,
            value,
            payment_type: paymentType,
            ...(coupon ? { coupon } : {}),
            items,
        },
    });
}

export function trackGA4Purchase(
    items: GA4Item[],
    currency: string,
    transactionId: string,
    value: number,
    tax: number,
    shipping: number,
    coupon?: string,
) {
    clearAndPush({
        event: "purchase",
        ecommerce: {
            transaction_id: transactionId,
            currency,
            value,
            tax,
            shipping,
            ...(coupon ? { coupon } : {}),
            items,
        },
    });
}

export function trackGA4AddToWishList(items: GA4Item[], currency: string, value: number) {
    clearAndPush({
        event: "add_to_wishlist",
        ecommerce: {
            currency,
            value,
            items,
        },
    });
}
